import {serializeUrl} from "@Utils/url.util";

export class SafeSearchFilter {
    static filterType = 'safe_content';
    static advancedFiltersArrayKey = SafeSearchFilter.filterType;
    static activeFilterOption = 'safe';

    static safeSearchActiveClasses = '-active';
    static safeSearchDisabledClasses = '-disabled';
    static safeSearchDisabledActiveClasses = '-disabled-active';
    static safeSearchInactiveClasses = '';
    static safeSearchLoadingInactiveClasses = '-loading-inactive';
    static safeSearchLoadingActiveClasses = '-loading-active';
    static safeSearchDisabledHiddenClasses = '_hidden';
    static safeSearchToggleContainerClasses = `${SafeSearchFilter.safeSearchActiveClasses} ${SafeSearchFilter.safeSearchInactiveClasses} ${SafeSearchFilter.safeSearchDisabledClasses} ${SafeSearchFilter.safeSearchLoadingInactiveClasses} ${SafeSearchFilter.safeSearchLoadingActiveClasses} ${SafeSearchFilter.safeSearchDisabledActiveClasses} ${SafeSearchFilter.safeSearchDisabledHiddenClasses}`;

    static DISABLED_STATE = 'disabled';
    static INACTIVE_STATE = 'inactive';
    static ACTIVE_STATE = 'active';
    static DISABLED_ACTIVE_STATE = 'disabled-active';
    static LOADING_INACTIVE_STATE = 'loading-inactive';
    static LOADING_ACTIVE_STATE = 'loading-active';
    static DISABLED_HIDDEN = 'disabled-hidden';

    /**
     * @return {boolean}
     */
    wasFilterUpdated() {
        return this.previousActiveState !== this.isActive;
    }

    resetOldFilterState() {
        return this.previousActiveState = this.isActive;
    }

    setDisabledState() {
        this.setState(this.isActive ? SafeSearchFilter.DISABLED_ACTIVE_STATE : SafeSearchFilter.DISABLED_STATE);
    }

    /**
     * @return {boolean}
     */
    isDisabledState() {
        return this.state === SafeSearchFilter.DISABLED_STATE;
    }

    setActiveState() {
        this.setState(this.isActive ? SafeSearchFilter.ACTIVE_STATE : SafeSearchFilter.INACTIVE_STATE);
    }

    /**
     * @return {boolean}
     */
    isActiveState() {
        return this.state === SafeSearchFilter.ACTIVE_STATE;
    }

    static getStateClassMapForSafeSearchToggle() {
        const map = {};
        map[this.INACTIVE_STATE] = this.safeSearchInactiveClasses;
        map[this.ACTIVE_STATE] = this.safeSearchActiveClasses;
        map[this.DISABLED_STATE] = this.safeSearchDisabledClasses;
        map[this.DISABLED_ACTIVE_STATE] = this.safeSearchDisabledActiveClasses;
        map[this.LOADING_ACTIVE_STATE] = this.safeSearchLoadingActiveClasses;
        map[this.LOADING_INACTIVE_STATE] = this.safeSearchLoadingInactiveClasses;
        map[this.DISABLED_HIDDEN] = this.safeSearchDisabledHiddenClasses;
        return map;
    }

    /**
     * @return {string}
     */
    getState() {
        return this.state;
    }

    /**
     * @param {string} state
     */
    setState(state) {
        if (this.isDisabledForStudent()) {
            return;
        }
        this.state = state;
    }

    /**
     *
     * @param {boolean} isActive
     */
    constructor(isActive) {
        this.isActive = isActive;

        this.previousActiveState = isActive;
        this.setActiveState();
    }

    /**
     * @return {SafeSearchFilter}
     */
    static fromPageHtml() {
        const safeSearchToggleContainer = $('.js-safe-content-toggle');
        return new this(safeSearchToggleContainer.hasClass(SafeSearchFilter.safeSearchActiveClasses), SafeSearchFilter.advancedFiltersArrayKey);
    }

    setLoadingState() {
        this.setState(this.isActive ? SafeSearchFilter.LOADING_ACTIVE_STATE : SafeSearchFilter.LOADING_INACTIVE_STATE);
    }

    /**
     * @return {string}
     */
    getActiveUriParam() {
        return SafeSearchFilter.activeFilterOption;
    }

    /**
     * @param {string} url
     * @return {string}
     */
    updateUrl(url) {
        const urlObj = new URL(url);
        return serializeUrl(this.updateSafeSearchQueryParam(urlObj));
    }

    static fromUrl(url) {
        const urlObj = new URL(url),
            safeSearchOption = urlObj.searchParams.get(SafeSearchFilter.filterType),
            isActiveInUrl = safeSearchOption === SafeSearchFilter.activeFilterOption;
        return new SafeSearchFilter(isActiveInUrl);
    }

    /**
     * @param {URL} urlObj
     * @return {URL}
     */
    updateSafeSearchQueryParam(urlObj) {
        if (this.isFilterActive()) {
            urlObj.searchParams.set(SafeSearchFilter.filterType, this.getActiveUriParam());
        } else {
            urlObj.searchParams.delete(SafeSearchFilter.filterType);
        }
        return urlObj;
    }

    /**
     * @param {string} url
     * @return {string}
     */
    removeSafeSearchFilterFromUrl(url) {
        const urlObj = new URL(url);
        urlObj.searchParams.delete(SafeSearchFilter.filterType);
        return serializeUrl(urlObj);
    }

    /**
     * @param {SafeSearchFilter} safeSearchObject
     */
    handleUpdatedFilter(safeSearchObject) {
        if (this.isActive === safeSearchObject.isActive) {
            return;
        }
        this.isActive = safeSearchObject.isActive;
        this.setActiveState();
        this.updateView();
    }

    /**
     * @param {SafeSearchFilter} safeSearchFilter
     */
    handleUpdatedFilterFromPopstate(safeSearchFilter) {
        this.isActive = safeSearchFilter.isActive;
        this.setActiveState();
    }

    /**
     * @return {boolean}
     */
    isFilterActive() {
        return this.isActive;
    }

    disableForStudent() {
        this.setState(SafeSearchFilter.DISABLED_HIDDEN);
        this.updateView();
    }

    /**
     * @return {boolean}
     */
    isDisabledForStudent() {
        return this.state === SafeSearchFilter.DISABLED_HIDDEN;
    }

    /**
     * @return {SafeSearchFilter}
     */
    cloneFilter() {
        const clonedFilter = new SafeSearchFilter(this.isActive);
        clonedFilter.setState(this.getState());
        return clonedFilter;
    }

    updateView() {
        const safeSearchToggleContainer = $('.js-safe-content-toggle'),
            stateClassMap = SafeSearchFilter.getStateClassMapForSafeSearchToggle(),
            stateClasses = stateClassMap[this.state];
        safeSearchToggleContainer.removeClass(SafeSearchFilter.safeSearchToggleContainerClasses).addClass(stateClasses);

        if (this.state === SafeSearchFilter.DISABLED_HIDDEN) {
            safeSearchToggleContainer.closest('.js-safe-content-filter-container').addClass('_hidden');
        }
        PMW.posterGallery.updateActiveFilterIcon();
    }

    /**
     * @return {boolean}
     */
    getPopStateInfo() {
        return this.isActive;
    }
}